import React, { useState, useEffect } from 'react';
import './JobsView.css';

function JobsView() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [error, setError] = useState(null);
  const jobsPerPage = 10;

  const [formData, setFormData] = useState({
    title: '',
    company: '',
    description: '',
    job_type: 'full-time',
    salary: '',
    salary_type: 'month',
    location: '',
    contact_email: '',
    contact_phone: '',
    is_active: true
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  // ✅ Helper: always get a fresh token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Token ${token}` } : {}),
    };
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);

      // ✅ FIXED: Pass Authorization header so Django accepts ?all=true (no more 401)
      const response = await fetch('https://imselling-marketplace-production.up.railway.app/api/jobs/?all=true', {
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        // Token expired or invalid — fall back to public endpoint
        console.warn('⚠️ Token invalid, falling back to public jobs endpoint');
        const fallback = await fetch('https://imselling-marketplace-production.up.railway.app/api/jobs/');
        const fallbackData = await fallback.json();
        const jobsArray = Array.isArray(fallbackData) ? fallbackData : (fallbackData.results || []);
        setJobs(jobsArray);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📦 Raw API response:', data);

      const jobsArray = Array.isArray(data) ? data : (data.results || []);
      console.log('✅ Jobs loaded:', jobsArray.length);
      setJobs(jobsArray);
    } catch (error) {
      console.error('❌ Error fetching jobs:', error);
      setError(error.message);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddJob = async () => {
    try {
      const payload = {
        ...formData,
        salary: formData.salary === '' ? null : formData.salary,
        contact_phone: formData.contact_phone || '',
        contact_email: formData.contact_email || '',
      };

      console.log('📤 Sending job payload:', payload);

      const response = await fetch('https://imselling-marketplace-production.up.railway.app/api/jobs/', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();
      console.log('📥 Response:', response.status, responseData);

      if (response.ok) {
        alert('✅ Job posted successfully!');
        setShowAddModal(false);
        fetchJobs();
        resetForm();
      } else {
        alert(`Error: ${JSON.stringify(responseData)}`);
      }
    } catch (error) {
      console.error('Error adding job:', error);
      alert('Failed to post job: ' + error.message);
    }
  };

  const handleEditJob = async () => {
    try {
      const payload = {
        ...formData,
        salary: formData.salary === '' ? null : formData.salary,
      };

      const response = await fetch(`https://imselling-marketplace-production.up.railway.app/api/jobs/${selectedJob.id}/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert('✅ Job updated successfully!');
        setShowEditModal(false);
        fetchJobs();
        resetForm();
      } else {
        const error = await response.json();
        alert(`Error: ${JSON.stringify(error)}`);
      }
    } catch (error) {
      console.error('Error updating job:', error);
      alert('Failed to update job');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;

    try {
      const response = await fetch(`https://imselling-marketplace-production.up.railway.app/api/jobs/${jobId}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok || response.status === 204) {
        alert('✅ Job deleted successfully!');
        fetchJobs();
      } else {
        alert('Failed to delete job');
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('Failed to delete job');
    }
  };

  const handleToggleStatus = async (job) => {
    try {
      const response = await fetch(`https://imselling-marketplace-production.up.railway.app/api/jobs/${job.id}/`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: !job.is_active })
      });

      if (response.ok) {
        fetchJobs();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const openEditModal = (job) => {
    setSelectedJob(job);
    setFormData({
      title: job.title || '',
      company: job.company || '',
      description: job.description || '',
      job_type: job.job_type || 'full-time',
      salary: job.salary || '',
      salary_type: job.salary_type || 'month',
      location: job.location || '',
      contact_email: job.contact_email || '',
      contact_phone: job.contact_phone || '',
      is_active: job.is_active
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      company: '',
      description: '',
      job_type: 'full-time',
      salary: '',
      salary_type: 'month',
      location: '',
      contact_email: '',
      contact_phone: '',
      is_active: true
    });
    setSelectedJob(null);
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch =
      job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.location?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'all' ||
      job.job_type?.toLowerCase() === filterType.toLowerCase();

    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && job.is_active) ||
      (filterStatus === 'inactive' && !job.is_active);

    return matchesSearch && matchesType && matchesStatus;
  });

  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

  const stats = {
    total: jobs.length,
    active: jobs.filter(j => j.is_active).length,
    inactive: jobs.filter(j => !j.is_active).length,
    fullTime: jobs.filter(j => j.job_type?.toLowerCase() === 'full-time').length
  };

  const formatJobType = (type) => {
    if (!type) return 'N/A';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatSalaryType = (type) => {
    const map = { 'month': 'Monthly', 'year': 'Yearly', 'hour': 'Hourly' };
    return map[type?.toLowerCase()] || type || 'Monthly';
  };

  return (
    <div className="jobs-view">
      {/* Header */}
      <div className="page-header-jobs">
        <div>
          <h1 className="page-title">Jobs Management</h1>
          <p className="page-subtitle">Manage all job listings on your platform</p>
        </div>
        <button className="btn-add-job" onClick={() => setShowAddModal(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2"/>
            <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Post New Job
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid-jobs">
        <div className="stat-card-job">
          <div className="stat-icon-job blue">💼</div>
          <div className="stat-content-job">
            <div className="stat-value-job">{stats.total}</div>
            <div className="stat-label-job">Total Jobs</div>
          </div>
        </div>
        <div className="stat-card-job">
          <div className="stat-icon-job green">✓</div>
          <div className="stat-content-job">
            <div className="stat-value-job">{stats.active}</div>
            <div className="stat-label-job">Active</div>
          </div>
        </div>
        <div className="stat-card-job">
          <div className="stat-icon-job red">✕</div>
          <div className="stat-content-job">
            <div className="stat-value-job">{stats.inactive}</div>
            <div className="stat-label-job">Inactive</div>
          </div>
        </div>
        <div className="stat-card-job">
          <div className="stat-icon-job purple">⏰</div>
          <div className="stat-content-job">
            <div className="stat-value-job">{stats.fullTime}</div>
            <div className="stat-label-job">Full-time</div>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div style={{
          background: '#fee2e2', color: '#dc2626', padding: '12px 16px',
          borderRadius: '8px', marginBottom: '16px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center'
        }}>
          <span>❌ Error loading jobs: {error}</span>
          <button onClick={fetchJobs} style={{
            background: '#dc2626', color: 'white', border: 'none',
            borderRadius: '4px', padding: '4px 12px', cursor: 'pointer'
          }}>Retry</button>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar-jobs">
        <div className="search-box-jobs">
          <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
          </svg>
          <input
            type="text"
            placeholder="Search jobs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="filter-select">
          <option value="all">All Types</option>
          <option value="full-time">Full-time</option>
          <option value="part-time">Part-time</option>
          <option value="contract">Contract</option>
          <option value="internship">Internship</option>
          <option value="temporary">Temporary</option>
        </select>

        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
          {jobs.length} total loaded
        </span>
      </div>

      {/* Jobs Table */}
      <div className="table-wrapper-jobs">
        {loading ? (
          <div className="loading-state">Loading jobs...</div>
        ) : currentJobs.length === 0 ? (
          <div className="empty-state-jobs">
            <div className="empty-icon">💼</div>
            <h3>No jobs found</h3>
            <p>
              {jobs.length > 0
                ? `${jobs.length} jobs exist but don't match filters.`
                : 'Try posting a new job.'}
            </p>
            {jobs.length > 0 && (
              <button
                onClick={() => { setSearchTerm(''); setFilterType('all'); setFilterStatus('all'); }}
                style={{
                  marginTop: '12px', padding: '8px 16px', background: '#6366f1',
                  color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'
                }}
              >Clear Filters</button>
            )}
          </div>
        ) : (
          <table className="jobs-table">
            <thead>
              <tr>
                <th>JOB TITLE</th>
                <th>COMPANY</th>
                <th>TYPE</th>
                <th>LOCATION</th>
                <th>SALARY</th>
                <th>STATUS</th>
                <th>VIEWS</th>
                <th>POSTED</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {currentJobs.map((job) => (
                <tr key={job.id}>
                  <td className="job-title-cell">
                    <div className="job-title">{job.title}</div>
                  </td>
                  <td>{job.company}</td>
                  <td>
                    <span className={`type-badge ${job.job_type?.toLowerCase().replace('-', '')}`}>
                      {formatJobType(job.job_type)}
                    </span>
                  </td>
                  <td className="location-cell">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    {job.location}
                  </td>
                  <td className="salary-cell">
                    {job.salary
                      ? `KES ${parseFloat(job.salary).toLocaleString()} / ${formatSalaryType(job.salary_type)}`
                      : 'Negotiable'}
                  </td>
                  <td>
                    <button
                      className={`status-toggle ${job.is_active ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus(job)}
                    >
                      {job.is_active ? '🟢 Active' : '🔴 Inactive'}
                    </button>
                  </td>
                  <td className="views-cell">{job.views || 0}</td>
                  <td className="date-cell">
                    {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td>
                    <div className="actions-cell">
                      <button className="action-btn edit" onClick={() => openEditModal(job)} title="Edit">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      </button>
                      <button className="action-btn delete" onClick={() => handleDeleteJob(job.id)} title="Delete">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-jobs">
          <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>
            Next
          </button>
        </div>
      )}

      {/* Add / Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{showEditModal ? 'Edit Job' : 'Post New Job'}</h3>
              <button onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>✕</button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Job Title *</label>
                  <input type="text" value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g. Senior Software Engineer" />
                </div>
                <div className="form-group">
                  <label>Company *</label>
                  <input type="text" value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    placeholder="Company name" />
                </div>
                <div className="form-group full-width">
                  <label>Description *</label>
                  <textarea value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Job description and requirements..." rows="5" />
                </div>
                <div className="form-group">
                  <label>Job Type *</label>
                  <select value={formData.job_type} onChange={(e) => setFormData({...formData, job_type: e.target.value})}>
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                    <option value="temporary">Temporary</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Location *</label>
                  <input type="text" value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="e.g. Nairobi, Kenya" />
                </div>
                <div className="form-group">
                  <label>Salary (Optional)</label>
                  <input type="number" value={formData.salary}
                    onChange={(e) => setFormData({...formData, salary: e.target.value})}
                    placeholder="e.g. 50000" />
                </div>
                <div className="form-group">
                  <label>Salary Type</label>
                  <select value={formData.salary_type} onChange={(e) => setFormData({...formData, salary_type: e.target.value})}>
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                    <option value="hour">Hourly</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Contact Email</label>
                  <input type="email" value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                    placeholder="hr@company.com" />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input type="tel" value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                    placeholder="0712345678" />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input type="checkbox" checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})} />
                    Active (visible to job seekers)
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel"
                onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>
                Cancel
              </button>
              <button className="btn-submit" onClick={showEditModal ? handleEditJob : handleAddJob}>
                {showEditModal ? 'Update Job' : 'Post Job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default JobsView;